import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaPaperPlane, FaUser, FaHeadset } from 'react-icons/fa';
import './TicketDetail.css';

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const isSupportTeam = user?.userType === 'support' || user?.userType === 'admin';

  useEffect(() => {
    fetchTicketDetail();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.conversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTicketDetail = async () => {
    try {
      const response = await api.get(`/api/support/tickets/${id}`);
      setTicket(response.data.ticket);
    } catch (error) {
      console.error('Failed to fetch ticket:', error);
      toast.error('Failed to load ticket details');
      navigate('/support');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSending(true);
    try {
      const response = await api.post(`/api/support/tickets/${id}/reply`, { message });
      setTicket(response.data.ticket);
      setMessage('');
      toast.success('Message sent successfully');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleAssignToMe = async () => {
    try {
      const response = await api.put(`/api/support/tickets/${id}/assign`, {
        assignedTo: user._id
      });
      setTicket(response.data.ticket);
      toast.success('Ticket assigned to you');
    } catch (error) {
      toast.error('Failed to assign ticket');
    }
  };

  const handleStatusChange = async (status) => {
    try {
      const response = await api.put(`/api/support/tickets/${id}/status`, { status });
      setTicket(response.data.ticket);
      toast.success(`Ticket status updated to ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleResolve = async () => {
    const resolutionMessage = prompt('Enter resolution message:');
    if (!resolutionMessage) return;

    try {
      const response = await api.put(`/api/support/tickets/${id}/resolve`, {
        resolutionMessage
      });
      setTicket(response.data.ticket);
      toast.success('Ticket resolved successfully');
    } catch (error) {
      toast.error('Failed to resolve ticket');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open': return 'badge-warning';
      case 'in_progress': return 'badge-info';
      case 'pending_support': return 'badge-danger';
      case 'pending_user': return 'badge-warning';
      case 'resolved': return 'badge-success';
      case 'closed': return 'badge-secondary';
      default: return 'badge-warning';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'urgent': return 'badge-danger';
      case 'high': return 'badge-warning';
      case 'medium': return 'badge-info';
      case 'low': return 'badge-secondary';
      default: return 'badge-info';
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    
    if (isToday) {
      return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) return <Layout><div className="loading">Loading ticket details...</div></Layout>;
  if (!ticket) return <Layout><div className="error">Ticket not found</div></Layout>;

  return (
    <Layout>
      <div className="ticket-detail-page">
        <div className="ticket-detail-header">
          <button className="btn btn-outline" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </button>
          <div className="ticket-header-info">
            <h1>{ticket.subject}</h1>
            <div className="ticket-meta">
              <span className="ticket-number">#{ticket.ticketNumber}</span>
              <span className={`badge ${getStatusBadgeClass(ticket.status)}`}>
                {ticket.status.replace('_', ' ').toUpperCase()}
              </span>
              <span className={`badge ${getPriorityBadgeClass(ticket.priority)}`}>
                {ticket.priority.toUpperCase()}
              </span>
              <span className="badge badge-secondary">{ticket.category}</span>
            </div>
          </div>
          {isSupportTeam && (
            <div className="ticket-actions">
              {!ticket.assignedTo && (
                <button className="btn btn-primary" onClick={handleAssignToMe}>
                  Assign to Me
                </button>
              )}
              {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={handleResolve}
                  >
                    Mark Resolved
                  </button>
                  <select 
                    className="status-select"
                    value={ticket.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="pending_user">Pending User</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </>
              )}
            </div>
          )}
        </div>

        <div className="ticket-detail-content">
          <div className="ticket-sidebar">
            <div className="info-card card">
              <h3>Ticket Information</h3>
              <div className="info-row">
                <span className="label">Created:</span>
                <span className="value">{new Date(ticket.createdAt).toLocaleString('en-IN')}</span>
              </div>
              <div className="info-row">
                <span className="label">Last Updated:</span>
                <span className="value">{new Date(ticket.updatedAt).toLocaleString('en-IN')}</span>
              </div>
              {ticket.assignedTo && (
                <div className="info-row">
                  <span className="label">Assigned To:</span>
                  <span className="value">{ticket.assignedTo.businessName || ticket.assignedTo.email}</span>
                </div>
              )}
              {ticket.lastResponseBy && (
                <div className="info-row">
                  <span className="label">Last Response:</span>
                  <span className="value">{ticket.lastResponseBy === 'support' ? 'Support Team' : 'User'}</span>
                </div>
              )}
            </div>

            <div className="info-card card">
              <h3>Customer Information</h3>
              <div className="info-row">
                <span className="label">Name:</span>
                <span className="value">{ticket.user.businessName}</span>
              </div>
              <div className="info-row">
                <span className="label">Email:</span>
                <span className="value">{ticket.user.email}</span>
              </div>
              <div className="info-row">
                <span className="label">Phone:</span>
                <span className="value">{ticket.user.phone}</span>
              </div>
              <div className="info-row">
                <span className="label">Type:</span>
                <span className="value">{ticket.userType || ticket.user?.userType}</span>
              </div>
            </div>

            {ticket.relatedOrder && (
              <div className="info-card card">
                <h3>Related Order</h3>
                <div className="info-row">
                  <span className="label">Order #:</span>
                  <span className="value">{ticket.relatedOrder.orderNumber}</span>
                </div>
              </div>
            )}

            {ticket.relatedProduct && (
              <div className="info-card card">
                <h3>Related Product</h3>
                <div className="info-row">
                  <span className="label">Product:</span>
                  <span className="value">{ticket.relatedProduct.title}</span>
                </div>
              </div>
            )}

            {ticket.resolution && ticket.resolution.resolvedAt && (
              <div className="info-card card resolution-card">
                <h3>Resolution</h3>
                <p>{ticket.resolution.message}</p>
                <div className="info-row">
                  <span className="label">Resolved By:</span>
                  <span className="value">{ticket.resolution.resolvedBy?.businessName}</span>
                </div>
                <div className="info-row">
                  <span className="label">Resolved At:</span>
                  <span className="value">{new Date(ticket.resolution.resolvedAt).toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}
          </div>

          <div className="conversation-section card">
            <h3>Conversation</h3>
            
            <div className="initial-message">
              <div className="message-header">
                <div className="sender-info">
                  <FaUser className="sender-icon" />
                  <span className="sender-name">{ticket.user.businessName}</span>
                  <span className="sender-badge">Customer</span>
                </div>
                <span className="message-time">{formatDate(ticket.createdAt)}</span>
              </div>
              <div className="message-content">
                <p><strong>Subject:</strong> {ticket.subject}</p>
                <p>{ticket.description}</p>
              </div>
            </div>

            <div className="conversation-messages">
              {ticket.conversation && ticket.conversation.length > 0 ? (
                ticket.conversation.map((msg, index) => {
                  const isSupportMessage = msg.sender?.userType === 'support' || msg.sender?.userType === 'admin';
                  const isMyMessage = msg.sender?._id === user?._id;
                  
                  return (
                    <div 
                      key={index} 
                      className={`message ${isSupportMessage ? 'support-message' : 'user-message'} ${isMyMessage ? 'my-message' : ''}`}
                    >
                      <div className="message-header">
                        <div className="sender-info">
                          {isSupportMessage ? (
                            <FaHeadset className="sender-icon support-icon" />
                          ) : (
                            <FaUser className="sender-icon" />
                          )}
                          <span className="sender-name">{msg.sender?.businessName || 'Unknown'}</span>
                          <span className={`sender-badge ${isSupportMessage ? 'support-badge' : ''}`}>
                            {isSupportMessage ? 'Support Team' : 'Customer'}
                          </span>
                        </div>
                        <span className="message-time">{formatDate(msg.timestamp)}</span>
                      </div>
                      <div className="message-content">
                        <p>{msg.message}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-messages">
                  <p>No replies yet. Start the conversation!</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {ticket.status !== 'closed' && (
              <form className="reply-form" onSubmit={handleSendMessage}>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Type your message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={sending || !message.trim()}
                >
                  <FaPaperPlane /> {sending ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TicketDetail;
